import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Tag } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name_en: "", name_ar: "", active: true, type: "both" };

export default function Categories() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [c, p, s] = await Promise.all([
      api.get("/product-categories"),
      api.get("/products"),
      api.get("/services"),
    ]);
    setItems(c.data); setProducts(p.data); setServices(s.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name_en || !form.name_ar) { toast.error(t("required_field")); return; }
    try {
      if (editId) await api.put(`/product-categories/${editId}`, form);
      else await api.post("/product-categories", form);
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الفئة؟" : "Delete category?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/product-categories/${id}`); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("in_use_cannot_delete"));
    }
  };

  // Wave B — count by binding kind
  const countItems = (catId, type) => {
    const usesP = type === "product" || type === "both";
    const usesS = type === "service" || type === "both";
    const np = usesP ? products.filter(p => p.category_id === catId).length : 0;
    const ns = usesS ? services.filter(s => s.category_id === catId).length : 0;
    return { products: np, services: ns };
  };
  const TYPE_LABELS = {
    service: { ar: "خدمات", en: "Services", cls: "bg-rose-100 text-rose-700 border-rose-300" },
    product: { ar: "منتجات", en: "Products", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    both:    { ar: "الكل",  en: "Both",     cls: "bg-stone-100 text-stone-700 border-stone-300" },
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-4xl gold-text">{t("nav_categories")}</h1>
        <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-category-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16}/>{t("add")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => {
          const typeKey = c.type || "both";
          const lbl = TYPE_LABELS[typeKey] || TYPE_LABELS.both;
          const cnt = countItems(c.category_id, typeKey);
          return (
            <div key={c.category_id} className="card-surface p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md border border-[var(--border)] flex items-center justify-center">
                  <Tag size={18} className="text-[var(--gold)]"/>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-lg text-[var(--text)]">{lang === "ar" ? c.name_ar : c.name_en}</div>
                    <span data-testid={`cat-type-pill-${c.category_id}`} className={`px-2 py-px rounded-full text-[10px] border font-medium ${lbl.cls}`}>
                      {lang === "ar" ? lbl.ar : lbl.en}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    {cnt.services > 0 && <span>{cnt.services} {lang === "ar" ? "خدمة" : "services"}</span>}
                    {cnt.services > 0 && cnt.products > 0 && " · "}
                    {cnt.products > 0 && <span>{cnt.products} {lang === "ar" ? "منتج" : "products"}</span>}
                    {cnt.services === 0 && cnt.products === 0 && <span>{t("no_data")}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setForm({...empty, ...c}); setEditId(c.category_id); setOpen(true); }} data-testid={`edit-cat-${c.category_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
                <button onClick={() => remove(c.category_id)} data-testid={`delete-cat-${c.category_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-[var(--text-muted)] text-center py-12">{t("no_data")}</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_en")}</label><input className="input-dark" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} data-testid="cat-name-en"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_ar")}</label><input className="input-dark" value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})} data-testid="cat-name-ar"/></div>
              <div className="md:col-span-2">
                <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "النوع" : "Type"}</label>
                <select className="input-dark" value={form.type || "both"} onChange={e => setForm({...form, type: e.target.value})} data-testid="cat-type-select">
                  <option value="both">{lang === "ar" ? "خدمات ومنتجات" : "Services & Products"}</option>
                  <option value="service">{lang === "ar" ? "خدمات فقط" : "Services only"}</option>
                  <option value="product">{lang === "ar" ? "منتجات فقط" : "Products only"}</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-5 md:col-span-2"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/><span className="text-sm text-[var(--text)]">{t("active")}</span></div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="cat-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
